import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Language, TranslationResult } from '../../../types';
import {
  cancelTranslateFileJob,
  createTranslateFileJob,
  fetchTranslationBlob,
  getTranslateFileJob,
} from '../api/translateFile.api';
import {
  ACTIVE_JOB_STATUSES,
  JOB_POLL_BACKOFF_FACTOR,
  JOB_POLL_INITIAL_SECONDS,
  JOB_POLL_MAX_ERRORS,
  JOB_POLL_MAX_SECONDS,
  TRANSLATE_ACTIVE_JOB_STORAGE_KEY,
} from '../constants';
import type { TranslateFileInput, TranslateFileJob } from '../types';

interface UseTranslateFileJobOptions {
  agentId: string;
  findLanguage: (code: string) => Language;
  onResults: (results: TranslationResult[]) => void;
}

export function isActiveJob(job: TranslateFileJob | null): boolean {
  return Boolean(job && (ACTIVE_JOB_STATUSES as readonly string[]).includes(job.status));
}

const readStoredJobId = (): string | null => {
  try {
    return window.localStorage.getItem(TRANSLATE_ACTIVE_JOB_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredJobId = (jobId: string | null) => {
  try {
    if (jobId) window.localStorage.setItem(TRANSLATE_ACTIVE_JOB_STORAGE_KEY, jobId);
    else window.localStorage.removeItem(TRANSLATE_ACTIVE_JOB_STORAGE_KEY);
  } catch {
    // Storage can be blocked; resuming after reload is a convenience only.
  }
};

/**
 * File translation via the async job API: create, poll with backoff, cancel, and resume the
 * last active job after a reload. Only one polling loop exists at a time (guarded by `runId`).
 */
export function useTranslateFileJob(options: UseTranslateFileJobOptions) {
  const { t } = useTranslation();
  const [job, setJob] = useState<TranslateFileJob | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const runId = useRef(0);
  const timer = useRef<number | undefined>(undefined);

  const stopPolling = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  const buildResults = useCallback(
    (
      jobId: string,
      langs: string[],
      status: TranslationResult['status'],
      sourceText: string
    ): TranslationResult[] =>
      langs.map((lang) => ({
        id: `${jobId}-${lang}`,
        targetLanguage: optionsRef.current.findLanguage(lang),
        translatedText: '',
        sourceText,
        sourceLanguage: optionsRef.current.findLanguage('en'),
        agentId: optionsRef.current.agentId,
        status,
        createdAt: new Date(),
      })),
    []
  );

  const finishJob = useCallback(
    async (finished: TranslateFileJob, token: number) => {
      const fileName = finished.source.fileName;

      if (finished.status === 'completed' && finished.translations) {
        setFinalizing(true);
        const results: TranslationResult[] = [];
        for (const item of finished.translations) {
          let translatedText = '';
          let previewUnavailable = false;
          try {
            translatedText = await (await fetchTranslationBlob(item.downloadUrl)).text();
          } catch {
            previewUnavailable = true;
          }
          results.push({
            id: `${finished.jobId}-${item.lang}`,
            targetLanguage: optionsRef.current.findLanguage(item.lang),
            translatedText,
            sourceText: fileName,
            sourceLanguage: optionsRef.current.findLanguage('en'),
            agentId: optionsRef.current.agentId,
            status: 'completed',
            createdAt: new Date(),
            previewUnavailable,
            download: { downloadUrl: item.downloadUrl, fileName: item.fileName },
          });
        }
        if (token !== runId.current) return;
        optionsRef.current.onResults(results);
        setFinalizing(false);
      } else if (finished.status === 'cancelled') {
        optionsRef.current.onResults([]);
        setInfoMessage(t('translate.job.cancelled'));
      } else {
        optionsRef.current.onResults(
          buildResults(finished.jobId, finished.targetLang, 'failed', fileName)
        );
        setErrorMessage(finished.error?.message || t('translate.errors.failed'));
      }
      writeStoredJobId(null);
    },
    [buildResults, t]
  );

  const poll = useCallback(
    (jobId: string, token: number, delaySeconds: number, failures: number) => {
      stopPolling();
      timer.current = window.setTimeout(async () => {
        if (token !== runId.current) return;
        try {
          const latest = await getTranslateFileJob(jobId);
          if (token !== runId.current) return;
          setJob(latest);
          if (isActiveJob(latest)) {
            poll(
              jobId,
              token,
              Math.min(delaySeconds * JOB_POLL_BACKOFF_FACTOR, JOB_POLL_MAX_SECONDS),
              0
            );
          } else {
            await finishJob(latest, token);
          }
        } catch {
          if (token !== runId.current) return;
          if (failures + 1 >= JOB_POLL_MAX_ERRORS) {
            // Keep the stored job id so a reload can pick the job up again.
            setJob(null);
            setErrorMessage(t('translate.errors.pollFailed'));
            return;
          }
          poll(jobId, token, delaySeconds, failures + 1);
        }
      }, delaySeconds * 1000);
    },
    [finishJob, stopPolling, t]
  );

  const track = useCallback(
    (current: TranslateFileJob, token: number) => {
      setJob(current);
      if (isActiveJob(current)) poll(current.jobId, token, JOB_POLL_INITIAL_SECONDS, 0);
      else void finishJob(current, token);
    },
    [finishJob, poll]
  );

  const start = useCallback(
    async (input: TranslateFileInput) => {
      const token = ++runId.current;
      stopPolling();
      setErrorMessage(null);
      setInfoMessage(null);
      setSubmitting(true);
      optionsRef.current.onResults(
        buildResults('pending', input.targetLang, 'processing', input.file.name)
      );

      try {
        const created = await createTranslateFileJob(input, globalThis.crypto?.randomUUID?.());
        if (token !== runId.current) return;
        writeStoredJobId(created.jobId);
        optionsRef.current.onResults(
          buildResults(created.jobId, created.targetLang, 'processing', created.source.fileName)
        );
        track(created, token);
      } catch (error) {
        if (token !== runId.current) return;
        setErrorMessage((error as Error).message || t('translate.errors.failed'));
        optionsRef.current.onResults(
          buildResults('pending', input.targetLang, 'failed', input.file.name)
        );
      } finally {
        if (token === runId.current) setSubmitting(false);
      }
    },
    [buildResults, stopPolling, t, track]
  );

  const cancel = useCallback(async () => {
    if (!job || cancelling) return;
    setCancelling(true);
    try {
      const updated = await cancelTranslateFileJob(job.jobId);
      track(updated, runId.current);
    } catch (error) {
      setErrorMessage((error as Error).message || t('translate.errors.failed'));
    } finally {
      setCancelling(false);
    }
  }, [cancelling, job, t, track]);

  const clearMessages = useCallback(() => {
    setErrorMessage(null);
    setInfoMessage(null);
  }, []);

  useEffect(() => {
    const storedJobId = readStoredJobId();
    const token = ++runId.current;

    if (storedJobId) {
      void getTranslateFileJob(storedJobId)
        .then((resumed) => {
          if (token !== runId.current) return;
          optionsRef.current.onResults(
            buildResults(resumed.jobId, resumed.targetLang, 'processing', resumed.source.fileName)
          );
          if (isActiveJob(resumed)) setInfoMessage(t('translate.job.resumed'));
          track(resumed, token);
        })
        .catch(() => {
          if (token === runId.current) writeStoredJobId(null);
        });
    }

    return () => {
      runId.current += 1;
      stopPolling();
    };
    // Resume only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    job,
    isActive: isActiveJob(job),
    isBusy: submitting || finalizing || isActiveJob(job),
    cancelling,
    errorMessage,
    infoMessage,
    start,
    cancel,
    clearMessages,
  };
}
